import React, { useState } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Clock, 
  AlertCircle, 
  CheckCircle,
  Pill,
  Calendar,
  Bell
} from 'lucide-react';
import toast from 'react-hot-toast';
import './Medications.css';

const Medications = () => {
  const [medications, setMedications] = useState([
    {
      id: 1,
      name: 'Aspirin',
      dosage: '100mg',
      frequency: 'daily',
      time: '09:00',
      startDate: '2024-01-01',
      endDate: '2024-12-31',
      instructions: 'Take with food',
      active: true,
      takenToday: true
    },
    {
      id: 2,
      name: 'Vitamin D',
      dosage: '1000IU',
      frequency: 'daily',
      time: '12:00',
      startDate: '2024-01-01',
      endDate: '2024-12-31',
      instructions: 'Take with breakfast',
      active: true,
      takenToday: false
    },
    {
      id: 3,
      name: 'Metformin',
      dosage: '500mg',
      frequency: 'twice daily',
      time: '08:00, 18:00',
      startDate: '2024-01-01',
      endDate: '2024-12-31',
      instructions: 'Take with meals',
      active: true,
      takenToday: false
    }
  ]);

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingMedication, setEditingMedication] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    dosage: '',
    frequency: 'daily',
    time: '',
    startDate: '',
    endDate: '',
    instructions: ''
  });

  const handleAddMedication = () => {
    if (!formData.name || !formData.dosage || !formData.time) {
      toast.error('Please fill in all required fields');
      return;
    }

    const newMedication = {
      id: Date.now(),
      ...formData,
      active: true,
      takenToday: false
    };

    setMedications([...medications, newMedication]);
    setFormData({
      name: '',
      dosage: '',
      frequency: 'daily',
      time: '',
      startDate: '',
      endDate: '',
      instructions: ''
    });
    setShowAddForm(false);
    toast.success('Medication added successfully!');
  };

  const handleEditMedication = (medication) => {
    setEditingMedication(medication);
    setFormData({
      name: medication.name,
      dosage: medication.dosage,
      frequency: medication.frequency,
      time: medication.time,
      startDate: medication.startDate,
      endDate: medication.endDate,
      instructions: medication.instructions
    });
    setShowAddForm(true);
  };

  const handleUpdateMedication = () => {
    if (!formData.name || !formData.dosage || !formData.time) {
      toast.error('Please fill in all required fields');
      return;
    }

    setMedications(medications.map(med => 
      med.id === editingMedication.id ? { ...med, ...formData } : med
    ));
    
    setEditingMedication(null);
    setFormData({
      name: '',
      dosage: '',
      frequency: 'daily',
      time: '',
      startDate: '',
      endDate: '',
      instructions: ''
    });
    setShowAddForm(false);
    toast.success('Medication updated successfully!');
  };

  const handleDeleteMedication = (id) => {
    setMedications(medications.filter(med => med.id !== id));
    toast.success('Medication deleted successfully!');
  };

  const toggleMedicationStatus = (id) => {
    setMedications(medications.map(med => 
      med.id === id ? { ...med, takenToday: !med.takenToday } : med
    ));
    toast.success('Medication status updated!');
  };

  const getTimeStatus = (time) => {
    const now = new Date();
    const times = time.split(', ');
    const currentTime = now.getHours() * 60 + now.getMinutes();
    
    for (let t of times) {
      const [hours, minutes] = t.split(':');
      const medTime = parseInt(hours) * 60 + parseInt(minutes);
      const diff = currentTime - medTime;
      
      if (Math.abs(diff) <= 30) return 'due';
      if (diff > 30) return 'overdue';
    }
    return 'upcoming';
  };

  return (
    <div className="container">
      <div className="page-header">
        <h1 className="page-title">Medications</h1>
        <p className="page-subtitle">Manage your medication schedule and reminders</p>
      </div>

      <div className="medications-container">
        <div className="medications-header">
          <div className="medications-stats">
            <div className="stat-item">
              <Pill size={20} />
              <span>{medications.length} Active</span>
            </div>
            <div className="stat-item">
              <Clock size={20} />
              <span>{medications.filter(m => !m.takenToday).length} Due Today</span>
            </div>
          </div>
          
          <button 
            className="btn btn-primary"
            onClick={() => setShowAddForm(true)}
          >
            <Plus size={16} />
            Add Medication
          </button>
        </div>

        {showAddForm && (
          <div className="add-medication-form">
            <h3>{editingMedication ? 'Edit Medication' : 'Add New Medication'}</h3>
            
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Medication Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Aspirin"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Dosage *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.dosage}
                  onChange={(e) => setFormData({...formData, dosage: e.target.value})}
                  placeholder="e.g., 100mg"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Frequency</label>
                <select
                  className="form-input"
                  value={formData.frequency}
                  onChange={(e) => setFormData({...formData, frequency: e.target.value})}
                >
                  <option value="daily">Daily</option>
                  <option value="twice daily">Twice Daily</option>
                  <option value="three times daily">Three Times Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="as needed">As Needed</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Time(s) *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.time}
                  onChange={(e) => setFormData({...formData, time: e.target.value})}
                  placeholder="e.g., 09:00 or 08:00, 18:00"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Start Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={formData.startDate}
                  onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label className="form-label">End Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={formData.endDate}
                  onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Instructions</label>
              <textarea
                className="form-input"
                value={formData.instructions}
                onChange={(e) => setFormData({...formData, instructions: e.target.value})}
                placeholder="e.g., Take with food"
                rows="3"
              />
            </div>

            <div className="form-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setShowAddForm(false);
                  setEditingMedication(null);
                  setFormData({
                    name: '',
                    dosage: '',
                    frequency: 'daily',
                    time: '',
                    startDate: '',
                    endDate: '',
                    instructions: ''
                  });
                }}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={editingMedication ? handleUpdateMedication : handleAddMedication}
              >
                {editingMedication ? 'Update' : 'Add'} Medication
              </button>
            </div>
          </div>
        )}

        <div className="medications-list">
          {medications.map((medication) => {
            const timeStatus = getTimeStatus(medication.time);
            return (
              <div key={medication.id} className="medication-card">
                <div className="medication-header">
                  <div className="medication-info">
                    <h3 className="medication-name">{medication.name}</h3>
                    <p className="medication-dosage">{medication.dosage}</p>
                  </div>
                  
                  <div className="medication-actions">
                    <button
                      className={`status-btn ${medication.takenToday ? 'taken' : ''}`}
                      onClick={() => toggleMedicationStatus(medication.id)}
                    >
                      {medication.takenToday ? (
                        <CheckCircle size={20} />
                      ) : (
                        <Clock size={20} />
                      )}
                    </button>
                    
                    <button
                      className="action-btn"
                      onClick={() => handleEditMedication(medication)}
                    >
                      <Edit size={16} />
                    </button>
                    
                    <button
                      className="action-btn delete"
                      onClick={() => handleDeleteMedication(medication.id)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                <div className="medication-details">
                  <div className="detail-item">
                    <Calendar size={16} />
                    <span>{medication.frequency}</span>
                  </div>
                  
                  <div className="detail-item">
                    <Clock size={16} />
                    <span>{medication.time}</span>
                  </div>
                  
                  <div className={`detail-item status ${timeStatus}`}>
                    {timeStatus === 'overdue' && <AlertCircle size={16} />}
                    {timeStatus === 'due' && <Clock size={16} />}
                    {timeStatus === 'upcoming' && <Bell size={16} />}
                    <span className="capitalize">{timeStatus}</span>
                  </div>
                </div>

                {medication.instructions && (
                  <div className="medication-instructions">
                    <strong>Instructions:</strong> {medication.instructions}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {medications.length === 0 && (
          <div className="empty-state">
            <Pill size={64} className="empty-state-icon" />
            <h3>No medications added yet</h3>
            <p>Add your first medication to start tracking your health</p>
            <button 
              className="btn btn-primary"
              onClick={() => setShowAddForm(true)}
            >
              <Plus size={16} />
              Add Your First Medication
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Medications; 